import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import RaaAuthenticatedRemoteService from '../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../envConfig';
import { selectHasValidPromoEngagementDiscount, selectServicePlan } from '../../redux/slices/billingSlice/billingSlice';

export const NOTIFICATION_TYPES = {
  TRIAL_ENDING_SOON: 'TRIAL_ENDING_SOON',
  SERVICE_LAPSED: 'SERVICE_LAPSED',
  NO_PAYMENT_METHOD: 'NO_PAYMENT_METHOD',
};

const MESSAGE_MAP = {
  // eslint-disable-next-line react/prop-types
  TRIAL_ENDING_SOON: ({ daysTillTrialEnds, discountedMonthlyPrice }) => (
    <>
      Your trial ends in
      {' '}
      {daysTillTrialEnds}
      {daysTillTrialEnds === 1 ? ' day' : ' days'}
      .
      {' '}
      <a href="/s/subscription/billing" className="text-white" style={{ textDecoration: 'underline' }}>
        Subscribe now
      </a>
      {' '}
      for less than $
      {discountedMonthlyPrice}
      {' '}
      per month.
    </>
  ),

  // eslint-disable-next-line react/prop-types
  SERVICE_LAPSED: ({ lapsedDate }) => (
    <>
      Your service lapsed on
      {' '}
      {lapsedDate}
      .
      {' '}
      <a href="/pricing" className="text-white" style={{ textDecoration: 'underline' }}>
        See plan options.
      </a>
      .
    </>
  ),

  // eslint-disable-next-line react/prop-types
  NO_PAYMENT_METHOD: ({ daysTillRenewal }) => (
    <>
      Your subscription renews in
      {' '}
      {daysTillRenewal}
      {' '}
      days.
      {' '}
      <a href="/s/subscription/billing" className="text-white" style={{ textDecoration: 'underline' }}>
        Arrange payment
      </a>
      {' '}
      to prevent losing service.
    </>
  ),
};

const deriveNotificationFromPlan = (accountStatus, hasValidPromoEngagement, servicePlan) => {
  if (!accountStatus) return null;

  const {
    expired,
    expirationDate,
    hasUpcomingRenewal,
    hasPaymentMethodOnFile,
    renewalDate,
  } = accountStatus;
  if (hasValidPromoEngagement && expirationDate && servicePlan) {
    const {
      recurringInterval,
      recurringAmount,
      promotionalEngagement,
    } = servicePlan;

    const discount = promotionalEngagement && promotionalEngagement.discount
      ? promotionalEngagement.discount
      : 0;

    const trialEndDate = servicePlan && servicePlan.endTime;
    const isYearlyInterval = recurringInterval === 'year' || recurringInterval === 'years';
    const baseMonthlyAmount = isYearlyInterval
      ? recurringAmount / 12
      : recurringAmount;

    const discountedMonthlyPrice = (baseMonthlyAmount * (1 - discount)).toFixed(2);
    if (hasValidPromoEngagement && trialEndDate) {
      const daysLeft = Math.ceil(
        (new Date(trialEndDate * 1000) - new Date()) / (1000 * 60 * 60 * 24),
      );

      if (daysLeft <= 14) {
        return {
          type: NOTIFICATION_TYPES.TRIAL_ENDING_SOON,
          data: {
            daysTillTrialEnds: daysLeft,
            discountedMonthlyPrice,
          },
        };
      }
    }

    if (expired) {
      return {
        type: NOTIFICATION_TYPES.SERVICE_LAPSED,
        data: {
          lapsedDate: moment.utc(expirationDate * 1000).format('MMM DD, YYYY'),
        },
      };
    }

    if (hasUpcomingRenewal && !hasPaymentMethodOnFile) {
      const daysTillRenewal = Math.ceil(
        (new Date(renewalDate * 1000) - new Date()) / (1000 * 60 * 60 * 24),
      );

      return {
        type: NOTIFICATION_TYPES.NO_PAYMENT_METHOD,
        data: { daysTillRenewal },
      };
    }
  }
  return null;
};

const getStorageKey = (type) => `notification_closed_${type}`;

const AccountStatusNotificationBar = () => {
  const hasValidPromoEngagement = useSelector(selectHasValidPromoEngagementDiscount);
  const servicePlan = useSelector(selectServicePlan);
  const [visible, setVisible] = useState(false);
  const [accountStatusDetails, setAccountStatusDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const res = await RaaAuthenticatedRemoteService.globalInstance().get(`${envConfig.apiUrl}/api/v1/billing/account-status`);

        if (!isMounted) return;
        setAccountStatusDetails(res && res.accountStatus);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('error: ', e, '');
        // silent fail
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const notification = deriveNotificationFromPlan(
    accountStatusDetails,
    hasValidPromoEngagement,
    servicePlan,
  );

  useEffect(() => {
    if (!notification) return;

    const isClosed = localStorage.getItem(getStorageKey(notification.type)) === 'true';
    setVisible(!isClosed);
  }, [notification]);

  if (loading && !accountStatusDetails) return null;
  if (!notification) return null;

  const { type, data } = notification;
  const renderMessage = MESSAGE_MAP[type];

  const handleClose = () => {
    localStorage.setItem(getStorageKey(type), 'true');
    setVisible(false);
  };

  if (!visible || !renderMessage) return null;

  return (
    <div className="w-100 bg-danger text-white d-flex align-items-center px-3 py-2">
      <div className="d-flex align-items-center justify-content-center flex-grow-1">
        <span>
          <FontAwesomeIcon icon={faCalendar} className="mr-1" />
        </span>
        <span className="small">{renderMessage(data)}</span>
      </div>

      <button
        type="button"
        onClick={handleClose}
        className="btn btn-sm btn-link text-white p-0 ml-auto"
      >
        ✕
      </button>
    </div>
  );
};

export default AccountStatusNotificationBar;
